import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import api from '../hooks/api';

const emptyForm = {
  name: '', emp_code: '', email: '', phone: '', designation: '',
  branch_id: '', department_id: '', joined_at: '', is_active: true
};

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modal, setModal] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterDept, setFilterDept] = useState('');

  const loadEmployees = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterBranch && user?.role === 'admin') params.set('branch_id', filterBranch);
    if (filterDept) params.set('department_id', filterDept);
    setEmployees(await api.get('/api/employees' + (params.toString() ? '?' + params : '')));
  }, [filterBranch, filterDept, user]);

  const loadMeta = useCallback(async () => {
    const [b, d] = await Promise.all([api.get('/api/branches'), api.get('/api/departments')]);
    setBranches(b); setDepartments(d);
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const filteredDepts = departments.filter(d => !form.branch_id || d.branch_id === form.branch_id);
  const filterDeptOptions = departments.filter(d => !filterBranch || d.branch_id === filterBranch);

  async function openAdd() {
    setError('');
    try {
      const { emp_code } = await api.get('/api/employees/next-code');
      setForm({ ...emptyForm, emp_code });
      setEditId(null);
      setModal(true);
    } catch (err) { alert(err.message); }
  }

  function openEdit(e) {
    setForm({
      name: e.name, emp_code: e.emp_code || '', email: e.email || '',
      phone: e.phone || '', designation: e.designation || '',
      branch_id: e.branch_id, department_id: e.department_id,
      joined_at: e.joined_at || '', is_active: !!e.is_active
    });
    setEditId(e.id); setError(''); setModal(true);
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault(); setError('');
    try {
      const payload = { ...form };
      if (editId) await api.put('/api/employees/' + editId, payload);
      else await api.post('/api/employees', payload);
      setModal(null); loadEmployees();
    } catch (err) { setError(err.message); }
  }

  async function handleExport() {
    const params = new URLSearchParams();
    if (filterBranch && user?.role === 'admin') params.set('branch_id', filterBranch);
    if (filterDept) params.set('department_id', filterDept);
    if (search) params.set('search', search);
    const today = new Date().toISOString().slice(0, 10);
    try { await api.download('/api/employees/export' + (params.toString() ? '?' + params : ''), `employees-${today}.csv`); }
    catch (err) { alert(err.message); }
  }

  async function handleDeactivate(emp) {
    if (!window.confirm(`Deactivate employee ${emp.name}?`)) return;
    await api.delete('/api/employees/' + emp.id);
    loadEmployees();
  }

  const visible = employees.filter(emp => {
    if (!search) return true;
    const q = search.toLowerCase();
    return emp.name.toLowerCase().includes(q) || (emp.emp_code || '').toLowerCase().includes(q) || (emp.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Employees</h1>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={handleExport}>Export CSV</button>
          {(user?.role === 'admin' || user?.role === 'manager') &&
            <button className="btn btn-primary" onClick={openAdd}>+ Add Employee</button>}
        </div>
      </div>
      <div className="filters">
        <input placeholder="Search name, code, email…" value={search} onChange={e => setSearch(e.target.value)} />
        {user?.role === 'admin' && (
          <select value={filterBranch} onChange={e => { setFilterBranch(e.target.value); setFilterDept(''); }}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}>
          <option value="">All Departments</option>
          {filterDeptOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Emp Code</th><th>Name</th><th>Designation</th><th>Branch</th>
              <th>Department</th><th>Contact</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(e => (
              <tr key={e.id}>
                <td className="mono">{e.emp_code || '—'}</td>
                <td>{e.name}</td>
                <td>{e.designation || '—'}</td>
                <td>{e.branch_name}</td>
                <td>{e.department_name}</td>
                <td>{e.email || e.phone || '—'}</td>
                <td>
                  <span className={`badge ${e.is_active ? 'badge-active' : 'badge-inactive'}`}>
                    {e.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(user?.role === 'admin' || user?.role === 'manager') && <>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(e)}>Edit</button>
                    {e.is_active &&
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => handleDeactivate(e)}>Deactivate</button>}
                  </>}
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={8} className="empty">No employees found</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={editId ? 'Edit Employee' : 'Add Employee'} onClose={() => setModal(null)}>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Emp Code {editId ? '' : '(auto-generated)'}</label>
                <input value={form.emp_code} disabled style={{ fontFamily: 'var(--mono)', background: 'var(--surface2)' }} />
              </div>
              <div className="form-group">
                <label>Designation</label>
                <input value={form.designation} onChange={e => set('designation', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Branch *</label>
              <select value={form.branch_id} onChange={e => { set('branch_id', e.target.value); set('department_id', ''); }} required>
                <option value="">Select branch…</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Department *</label>
              <select value={form.department_id} onChange={e => set('department_id', e.target.value)} required>
                <option value="">Select department…</option>
                {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Joined At</label>
              <input type="date" value={form.joined_at} onChange={e => set('joined_at', e.target.value)} />
            </div>
            {editId && (
              <div className="form-check">
                <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
                <label htmlFor="is_active">Active</label>
              </div>
            )}
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary">{editId ? 'Save Changes' : 'Add Employee'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
